import _ from 'lodash';

import { EXTRAS } from '../consts';
import moment from 'moment';

import ManagementConstants from '@/constants/ManagementConstants';

const format = ManagementConstants.DATE_PICKER_FORMAT;

export const getFilterName = (key, t) => {
	if (key === EXTRAS.TRAVEL_BOOSTER_NOT_SYNCED) {
		return t('Bookings.Filter.Extra');
	}

	if (key === EXTRAS.AGENCY_BRANCHES || key === EXTRAS.BOOKINGS_FROM_BRANCH) {
		return t('Bookings.Filter.Branch');
	}

	if (key === EXTRAS.CONTRACT_ID) {
		return t('Bookings.Filter.Company Name');
	}

	if (key === EXTRAS.TRAVELER_FIRST_NAME) {
		return t('Bookings.Filter.TravelerFirstName');
	}

	if (key === EXTRAS.TRAVELER_LAST_NAME) {
		return t('Bookings.Filter.TravelerLastName');
	}

	return t(`Bookings.Filter.${_.startCase(key)}`);
};

export const getLabel = (key, filters, t, profile = {}) => {
	const truncate = (label) => {
		if (!label) return '';
		const maxLength = 55;
		return label.length > maxLength ? `${label.slice(0, maxLength)}...` : label;
	};

	if (key === EXTRAS.TRAVEL_BOOSTER_NOT_SYNCED) {
		return truncate(t('Bookings.Filter.TravelBoosterNotSynced'));
	}

	if (key === EXTRAS.BOOKINGS_FROM_BRANCH) {
		if (profile?.company_branches) {
			// super agent with dropdown
			const branchId = filters[key];
			const branch = _.find(profile?.company_branches, { _id: branchId });
			return branch ? branch.name : '';
		} else if (profile?.agency_branch) {
			// travel agent with one branch
			return profile?.agency_branch.name;
		}
	}

	if (key === EXTRAS.CONTRACT_ID) {
		const contractId = filters[key];
		// If contractId is an array, it means "All Companies" was selected
		if (Array.isArray(contractId)) {
			return t('AdvancedFilters.AllCompanies');
		}
		const contract = _.find(profile?.agency_contracts, { _id: contractId });
		return contract ? contract?.company?.name : '';
	}

	const rawLabel = moment.isMoment(filters[key])
		? filters[key].format(format)
		: Object.values(EXTRAS).includes(filters[key])
			? t(`Bookings.Filter.${_.startCase(filters[key])}`)
			: filters[key];

	return truncate(String(rawLabel || ''));
};

export const normalizeToSabreRoomType = (description) => {
	const sabreRoomTypeMap = [
		{ match: /junior suite/, code: 'JSUI' },
		{ match: /\bsuite\b/, code: 'SUI' },
		{ match: /studio/, code: 'STU' },
		{ match: /family/, code: 'FMLY' },
		{ match: /apartment/, code: 'APT' },
		{ match: /villa/, code: 'VLLA' },
		{ match: /loft/, code: 'LOFT' },
		{ match: /bungalow/, code: 'BUNG' },
		{ match: /cabin/, code: 'CABN' },
		{ match: /penthouse/, code: 'PENT' },
		{ match: /dorm/, code: 'DORM' },
		{ match: /triple/, code: 'TRPL' },
		{ match: /quad/, code: 'QUAD' },
		{ match: /single/, code: 'SNGL' },
		{ match: /double/, code: 'DBLB' },
		{ match: /twin/, code: 'TWIN' },
		{ match: /queen/, code: 'QUEE' },
		{ match: /king/, code: 'KING' },
	];

	if (!description || typeof description !== 'string') return 'RM';
	const normalized = description.toLowerCase().replace(/\s+/g, ' ').trim();

	for (const entry of sabreRoomTypeMap) {
		if (entry.match.test(normalized)) {
			return entry.code;
		}
	}

	return 'RM'; // fallback "Room"
};

function formatAMPM(date) {
	let hours = date.getUTCHours();
	const ampm = hours >= 12 ? 'PM' : 'AM';
	hours = hours % 12;
	hours = hours ? hours : 12;
	return hours + ampm;
}

function cancellationDateCode(dca) {
	if (!dca?.dealIsFullyRefundableTill) {
		return '';
	}
	const date = new Date(new Date(dca.dealIsFullyRefundableTill).getTime() - 60000);
	const day = date.getUTCDate().toString().padStart(2, '0');
	const month = date.toLocaleString('en-GB', { month: 'short' }).toUpperCase();
	const hourPart = formatAMPM(date);

	return `${day}${month}/${hourPart}`; // 27APR/9AM
}

function extractCancellationCode({ dca }) {
	if (!dca?.dealIsFullyRefundableTill) {
		return 'NONREF';
	}
	return cancellationDateCode(dca);
}

function extractCancellationString({ dca }) {
	if (!dca?.dealIsFullyRefundableTill) {
		return 'Non Refundable';
	}
	return cancellationDateCode(dca);
}

function getBoardCode(deal) {
	const hasBreakfast = !!_.get(deal, 'dca.heuristicOptions.includesBreakfast', false);
	const hasHalfBoard = !!_.get(deal, 'dca.heuristicOptions.isHalfBoard', false);
	const hasFullBoard = !!_.get(deal, 'dca.heuristicOptions.isFullBoard', false);
	const hasAllInclusive = !!_.get(deal, 'dca.heuristicOptions.isAllInclusive', false);

	if (hasAllInclusive) return 'All Inclusive';
	if (hasFullBoard) return 'Full Board';
	if (hasHalfBoard) return 'Half Board';
	if (hasBreakfast) return 'Bed & Breakfast';

	return 'Room Only';
}

const formatDate = (isoDate) => {
	const date = new Date(isoDate);
	const day = date.getDate().toString().padStart(2, '0');
	const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
	return `${day}${month}`;
};

export const generateSabreGdsSegment = (booking, { isBlueCubeCompany, isBTCCompany }) => {
	const { deal, hotel, short_id } = booking;

	const chainCode = 'YY'; // use "YY" if hotel chain is unknown
	const statusCode = 'GK'; // "GK" is used for passive segment
	const roomCount = deal.details.room_count || 1;
	const cityCode = hotel.sabro?.airport?.iata_code || hotel.city?.slice(0, 3).toUpperCase() || 'XXX';

	const inDate = formatDate(deal.details.check_in);
	const outDate = formatDate(deal.details.check_out);

	const hotelCode = ''; // for GK you usually leave this blank
	const hotelName = hotel.name.toUpperCase();

	const roomTypeCode = normalizeToSabreRoomType(deal.details.room_description);

	const currency = deal.cheapopoPricing.currency || 'USD';
	const price = deal.client_price_per_night?.toFixed(2) || '0.00';
	const priceSegment = `${currency}${price}`;
	let priceAndRoomTypeSegment;
	if (isBTCCompany) {
		priceAndRoomTypeSegment = `${roomTypeCode}¤${getBoardCode(deal).toUpperCase()}¤${priceSegment}`;
	} else {
		priceAndRoomTypeSegment = `${roomTypeCode}/${priceSegment}`;
	}

	const guarantee = 'G'; // defaulted to 'G'
	const address = hotel.address?.replace(',', '') ?? '';
	const supplementaryInfo =
		`SI-¤${address}¥${hotel.city}¥${hotel.zip || ''}¥FONE${hotel.phone?.replace(/[^0-9+]/g, '') || ''}`.toUpperCase();
	const cancelCode = isBlueCubeCompany
		? `Cancellation Policy - ${extractCancellationString(deal)}`
		: `CANX-${extractCancellationCode(deal)}`;
	const confirmation = `CF-${short_id.replaceAll(/[^A-Za-z0-9]/g, '')}`;

	return `0HHT${chainCode}${statusCode}${roomCount}/${cityCode}IN${inDate}-OUT${outDate}/${hotelCode}${hotelName}/${priceAndRoomTypeSegment}/${guarantee}/${supplementaryInfo}¤${cancelCode}/${confirmation}`;
};

const generateAmadeusRateCode = (deal) => {
	const dca = deal?.dca || {};
	const roomDesc = deal?.details?.room_description || '';
	const roomCode = normalizeToSabreRoomType(roomDesc);

	let board = '';
	if (dca.heuristicOptions?.includesBreakfast) board = 'B';
	if (dca.heuristicOptions?.isHalfBoard) board = 'HB';
	if (dca.heuristicOptions?.isFullBoard) board = 'FB';
	if (dca.heuristicOptions?.isAllInclusive) board = 'AI';

	let refund = '';
	if (dca.nonRefundable === true) refund = 'NR';
	else if (dca.nonRefundable === false) refund = 'RF';

	return `${roomCode}${board}${refund}`;
};

const generateAmadeusExtendedGdsSegment = (booking) => {
	const { deal, hotel, short_id } = booking;
	const propertyCode = hotel.amadeus?.id;

	const checkInDate = new Date(deal.details.check_in);
	const checkInDay = checkInDate.getDate().toString().padStart(2, '0');
	const checkInMonth = checkInDate.toLocaleString('en-US', { month: 'short' }).toUpperCase();
	const nights = deal.nights;

	const roomType = generateAmadeusRateCode(deal);
	const currency = deal.cheapopoPricing.currency || 'EUR';
	const price = deal.client_price_per_night?.toFixed(2) || '0.00';

	const confirmationNumber = short_id.replaceAll(/[^A-Za-z0-9]/g, '');

	return `11AHSXX${propertyCode} ${checkInDay}${checkInMonth}-${nights}/CF-${confirmationNumber}/RT-${roomType}/RQ-${currency}${price}`;
};

const generateAmadeusFreeTextGdsSegment = (booking) => {
	const { deal, hotel, short_id } = booking;
	const paxCount = deal.rooms || 1;
	const cityCode = hotel.amadeus?.city_code?.toUpperCase() || hotel.city?.slice(0, 3).toUpperCase() || 'XXX';

	const checkInFormatted = formatDate(deal.details.check_in);
	const checkOutFormatted = formatDate(deal.details.check_out);

	// Hotel name and confirmation
	const hotelName = hotel.name.toUpperCase();
	const confirmationNumber = short_id.replaceAll(/[^A-Za-z0-9]/g, '');

	return `HU1AHK${paxCount} ${cityCode} ${checkInFormatted}-${checkOutFormatted}/${hotelName}/CF-${confirmationNumber}`;
};

export const generateAmadeusGdsSegment = (booking, specialCompanies) => {
	if (specialCompanies.isEACompany) {
		return specialAmadeus(booking);
	}
	return generalAmadeus(booking);

	// Not using now
	// const hasHotelCode = !!booking.hotel?.amadeus?.id;
	//
	// if (hasHotelCode) {
	// 	return generateAmadeusExtendedGdsSegment(booking);
	// } else {
	// 	return generateAmadeusFreeTextGdsSegment(booking);
	// }
};

const generalAmadeus = (booking) => {
	const { deal, hotel, short_id } = booking;

	const paxCount = deal.rooms || 1;
	const cityCode = hotel.amadeus?.city_code?.toUpperCase() || hotel.city?.slice(0, 3).toUpperCase() || 'XXX';

	const checkInFormatted = formatDate(deal.details.check_in);
	const checkOutFormatted = formatDate(deal.details.check_out);

	const hotelName = hotel.name?.toUpperCase() || 'HOTEL';
	const hotelChain = hotel.chain?.toUpperCase() || '';
	const address = hotel.address?.replace(/,/g, ' ') || '';
	const phone = hotel.phone?.replace(/[^0-9+]/g, '') || '';

	const roomType = generateAmadeusRateCode(deal);
	const currency = deal.cheapopoPricing?.currency || 'USD';
	const totalPrice = deal.client_price_total?.toFixed(2) || '0.00';

	const confirmationNumber = short_id?.replaceAll(/[^A-Za-z0-9]/g, '') || '';

	let segment = `HU1AHK${paxCount}${cityCode}${checkInFormatted}-${checkOutFormatted}`;

	segment += '/ARBITRIP';

	segment += `/HN-${hotelName}`;

	if (hotelChain) {
		segment += `/HC-${hotelChain}`;
	}

	if (address) {
		segment += `/AD-${address}`;
	}

	if (phone) {
		segment += `/PH-+${phone}`;
	}

	segment += `/RT-${roomType}`;

	if (confirmationNumber) {
		segment += `/CF-${confirmationNumber}`;
	}

	segment += `/RQ-${currency}${totalPrice}`;

 return segment;
};

const specialAmadeus = (booking) => {
	const { deal = {}, hotel = {}, short_id = '' } = booking || {};

	const paxCount = deal.rooms || 1;
	const cityCode = hotel.amadeus?.city_code?.toUpperCase() || hotel.city?.slice(0, 3).toUpperCase() || 'XXX';

	const checkInFormatted = formatDate(deal?.details?.check_in);
	const checkOutFormatted = formatDate(deal?.details?.check_out);

	const hotelName = (hotel.name || 'HOTEL').toUpperCase();

	const street = (hotel.street || hotel.address || '').split(',')[0]?.trim().toUpperCase();
	const city = (hotel.city || '').trim().toUpperCase();
	const postal = (hotel.zip || hotel.postal_code || '').toString().trim().toUpperCase();
	const country = (hotel.country_name || hotel.country || '').toString().trim().toUpperCase();
	const addressParts = [hotelName];
	if (street) addressParts.push(street);
	let cityPostal = city;
	if (postal) cityPostal = city ? `${city}  ${postal}` : postal; // double space before postal when city exists
	if (cityPostal) addressParts.push(cityPostal);
	if (country) addressParts.push(country);
	const addressLine = addressParts.join(',');

	// 91248290 -EA agency IATA ID (https://store.iata.org/ieccacfree?refURL=http%3A%2F%2Fstore.iata.org%2Fieccacfree)
	// it’s the IATA number of the agency making the booking.
	// Every accredited travel agency has its own IATA code (like a unique ID).
	// This number will change depending on which agency issues the booking.
	const bsSegment = `/**-BS-91248290`;

	const hnSegment = `/HN-${hotelName}`;

	const currency = deal?.cheapopoPricing?.currency || deal?.currency || 'USD';
	const totalPrice = Number(deal?.client_price_total) || 0;
	const priceFormatted = totalPrice.toFixed(2);

	// -1 = unique suffix added per passenger/room, because:
	// Many hotels issue one confirmation number even if you book multiple rooms.
	// But the travel agency’s invoicing/back-office system needs a unique reference per traveler/room to handle accounting, invoicing, and reconciliation.
	// So they append -1, -2, -3, etc. to make each booking line item unique.
	const cfSegment = short_id ? `/CF-${short_id.toUpperCase()}-1` : '';

	let segment = `HU1AHK${paxCount}${cityCode}${checkInFormatted}-${checkOutFormatted}`;
	segment += `/${addressLine}`;
	segment += `${bsSegment}`;
	segment += `${hnSegment}`;
	segment += `/RQ-${currency}${priceFormatted}`;
	segment += `${cfSegment}`;

	// P1 refers to Passenger 1 in the PNR (Passenger Name Record).
	// In Amadeus, each passenger in the booking is numbered (P1, P2, etc.).
	// This doesn’t indicate the total number of guests — it’s a reference to which passenger this hotel booking belongs to.
	segment += `/P1`;
	return segment;
};

const normalizeToHumanRoomType = (description) => {
	const roomTypeMap = [
		{ match: /\bsuite\b/, code: 'suite' },
		{ match: /studio/, code: 'studio' },
		{ match: /family/, code: 'family' },
		{ match: /apartment/, code: 'apartment' },
		{ match: /villa/, code: 'villa' },
		{ match: /loft/, code: 'LOFT' },
		{ match: /bungalow/, code: 'bungalow' },
		{ match: /cabin/, code: 'cabin' },
		{ match: /penthouse/, code: 'penthouse' },
		{ match: /dorm/, code: 'dorm' },
		{ match: /triple/, code: 'triple' },
		{ match: /quad/, code: 'quad' },
		{ match: /single/, code: 'single' },
		{ match: /double/, code: 'double' },
		{ match: /twin/, code: 'TWIN' },
		{ match: /queen/, code: 'queen' },
		{ match: /king/, code: 'KING' },
	];

	if (!description || typeof description !== 'string') return 'ROOM';
	const normalized = description.toLowerCase().replace(/\s+/g, ' ').trim();

	for (const entry of roomTypeMap) {
		if (entry.match.test(normalized)) {
			return entry.code;
		}
	}

	return 'ROOM'; // fallback "Room"
};

export const generateAdditionForAmadeusGdsSegment = (booking, segment) => {
	// Ensure segment is prefixed with 'S' as required by Amadeus entries like RIR .../S2
	const normalizedSegment = segment ? (segment.trim().toUpperCase().startsWith('S') ? segment.trim().toUpperCase() : `S${segment.trim().toUpperCase()}`) : 'S1';
	const { deal = {}, hotel = {}, short_id = '' } = booking || {};
	const lines = [];

	const phone = hotel.phone?.replace(/[^0-9+]/g, '') || '';
	if (phone) {
		lines.push(`RIR PHONE NUMBER:${phone}/${normalizedSegment}`);
	}

	const roomTypeHuman = normalizeToHumanRoomType(_.get(deal, 'details.room_description'));
	if (roomTypeHuman) {
		lines.push(`RIR ROOM TYPE:${roomTypeHuman.toString().toUpperCase()}/${normalizedSegment}`);
	}

	const boardHuman = getBoardCode(deal); // returns strings like 'Room Only', 'Bed & Breakfast', etc.
	if (boardHuman) {
		lines.push(`RIR HOTEL RATE INCLUDES:${boardHuman.toUpperCase()}/${normalizedSegment}`);
	}

	if (short_id) {
		lines.push(`RIR CONFIRMATION NUMBER:${short_id.toUpperCase()}/${normalizedSegment}`);
	}

	let cancellationLine = '';
	const dca = deal?.dca || {};
	if (dca?.dealIsFullyRefundableTill) {
		const date = new Date(dca.dealIsFullyRefundableTill);
		const day = date.getUTCDate();
		const monthName = date.toLocaleString('en-GB', { month: 'long' }).toUpperCase();
		cancellationLine = `RIR CANCELLATION:REFUNDABLE BEFORE ${day} ${monthName}/${normalizedSegment}`;
	} else if (dca?.nonRefundable === true) {
		cancellationLine = `RIR CANCELLATION:NON REFUNDABLE/${normalizedSegment}`;
	}
	if (cancellationLine) {
		lines.push(cancellationLine);
	}

	return lines.join('\n');
}
